import React, { createContext, useContext, useState, useCallback } from "react";
import { TimeFilter } from "../../../utils/utils";

interface FilterState {
  timeFilter: TimeFilter;
  customStartDate: string;
  customEndDate: string;
  selectedVehicles: string[];
  selectedVehicleIds: string[];
  selectedMarketLoadIds: string[];
  selectedProviderIds: string[];
  selectedProjectIds: string[];
  selectedParties: string[];
  pickupLocation: string;
  dropLocation: string;
  operatorNames: string[];
  mechanicName: string[];
  garageName: string[];
  providerName: string;
  driverCleanerName: string;
}

interface ReportFilterContextType {
  filterState: FilterState;
  updateTimeFilter: (filter: TimeFilter) => void;
  updateDateRange: (startDate: string, endDate: string) => void;
  updateVehicles: (vehicles: string[]) => void;
  updateVehicleIds: (vehicleIds: string[]) => void;
  updateMarketLoadIds: (marketLoadIds: string[]) => void;
  updateProviderIds: (providerIds: string[]) => void;
  updateProjectIds: (projectIds: string[]) => void;
  updateParties: (parties: string[]) => void;
  updateLocations: (pickup: string, drop: string) => void;
  updateOperator: (names: string[]) => void;
  updateMechanic: (name: string[]) => void;
  updateGarage: (name: string[]) => void;
  updateProvider: (name: string) => void;
  updateDriverCleaner: (name: string) => void;
  clearFilters: () => void;
  resetFilters: () => void;
}

const defaultFilterState: FilterState = {
  timeFilter: "today",
  customStartDate: "",
  customEndDate: "",
  selectedVehicles: [],
  selectedVehicleIds: [],
  selectedMarketLoadIds: [],
  selectedProviderIds: [],
  selectedProjectIds: [],
  selectedParties: [],
  pickupLocation: "All",
  dropLocation: "All",
  operatorNames: [],
  mechanicName: [],
  garageName: [],
  providerName: "",
  driverCleanerName: "",
};

const ReportFilterContext = createContext<ReportFilterContextType | undefined>(
  undefined
);

export const ReportFilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [filterState, setFilterState] =
    useState<FilterState>(defaultFilterState);

  const updateTimeFilter = useCallback((filter: TimeFilter) => {
    setFilterState((prev) => ({ ...prev, timeFilter: filter }));
  }, []);

  const updateDateRange = useCallback((startDate: string, endDate: string) => {
    setFilterState((prev) => ({
      ...prev,
      customStartDate: startDate,
      customEndDate: endDate,
    }));
  }, []);

  const updateVehicles = useCallback((vehicles: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedVehicles: vehicles }));
  }, []);

  const updateVehicleIds = useCallback((vehicleIds: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedVehicleIds: vehicleIds }));
  }, []);

  const updateMarketLoadIds = useCallback((marketLoadIds: string[]) => {
    setFilterState((prev) => ({
      ...prev,
      selectedMarketLoadIds: marketLoadIds,
    }));
  }, []);

  const updateProviderIds = useCallback((providerIds: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedProviderIds: providerIds }));
  }, []);

  const updateProjectIds = useCallback((projectIds: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedProjectIds: projectIds }));
  }, []);

  const updateParties = useCallback((parties: string[]) => {
    setFilterState((prev) => ({ ...prev, selectedParties: parties }));
  }, []);

  const updateLocations = useCallback((pickup: string, drop: string) => {
    setFilterState((prev) => ({
      ...prev,
      pickupLocation: pickup,
      dropLocation: drop,
    }));
  }, []);

  const updateOperator = useCallback((names: string[]) => {
    setFilterState((prev) => ({ ...prev, operatorNames: names }));
  }, []);

  const updateMechanic = useCallback((name: string[]) => {
    setFilterState((prev) => ({ ...prev, mechanicName: name }));
  }, []);

  const updateGarage = useCallback((name: string[]) => {
    setFilterState((prev) => ({ ...prev, garageName: name }));
  }, []);

  const updateProvider = useCallback((name: string) => {
    setFilterState((prev) => ({ ...prev, providerName: name }));
  }, []);

  const updateDriverCleaner = useCallback((name: string) => {
    setFilterState((prev) => ({ ...prev, driverCleanerName: name }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterState(defaultFilterState);
  }, []);

  // Reset filters to default state
  const resetFilters = useCallback(() => {
    setFilterState(defaultFilterState);
  }, []);

  const value = {
    filterState,
    updateTimeFilter,
    updateDateRange,
    updateVehicles,
    updateVehicleIds,
    updateMarketLoadIds,
    updateProviderIds,
    updateProjectIds,
    updateParties,
    updateLocations,
    updateOperator,
    updateMechanic,
    updateGarage,
    updateProvider,
    updateDriverCleaner,
    clearFilters,
    resetFilters,
  };

  return (
    <ReportFilterContext.Provider value={value}>
      {children}
    </ReportFilterContext.Provider>
  );
};

export const useReportFilter = () => {
  const context = useContext(ReportFilterContext);
  if (context === undefined) {
    throw new Error(
      "useReportFilter must be used within a ReportFilterProvider"
    );
  }
  return context;
};
